import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';
import css from './CoverPanel.module.css';

/**
 * A detail drawn over the card it belongs to (core/detail.ts: a train's itinerary), as the old kiosk's
 * itinerary did: a header in the theme's panel colours (Panel's tokens), the content scrolling under it,
 * a close button. It closes by itself at `closesAt` (null: never), and a touch or a key on it keeps it
 * open (`onTouch`); Escape closes it. The card must be `position: relative`.
 */
export function CoverPanel({ title, icon, closeLabel, closesAt, onClose, onTouch, headerClassName, children }: {
  title: string;
  icon?: string;
  closeLabel: string;
  closesAt: number | null;
  onClose: () => void;
  onTouch: () => void;
  /** The card's own header look, so the panel's header lines up with the one it covers. */
  headerClassName?: string;
  children: ReactNode;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (closesAt === null) return;
    const timer = setTimeout(onClose, Math.max(0, closesAt - Date.now()));
    return () => clearTimeout(timer);
  }, [closesAt, onClose]);

  // opened, the focus goes in, so a keyboard or a screen reader is where the detail is
  useEffect(() => closeButton.current?.focus({ preventScroll: true }), []);

  return (
    <section className={css.cover} aria-label={title} onPointerDownCapture={onTouch} onWheelCapture={onTouch}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); else onTouch(); }}
      onClick={(e) => e.stopPropagation()}>
      <header className={`${css.header} ${headerClassName ?? ''}`}>
        {icon && <Icon name={icon} size={26} />}
        <h3>{title}</h3>
        <button ref={closeButton} type="button" className={css.close} onClick={onClose} aria-label={closeLabel}>
          <Icon name="close" size={26} />
        </button>
      </header>
      <div className={css.body}>{children}</div>
    </section>
  );
}
